import React, { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Star, Trash2, Upload, Save, Image as ImageIcon, Plus, RotateCcw, Undo2 } from "lucide-react";
import { api } from "./AuthContext";
import { useBuildings, toAbsUrl } from "../data/ApartmentDataContext";
import { getApartmentSpec, PROJECT_SPEC_DEFAULTS } from "../data/apartments";

const GOLD = "#D4AF37";
const INV_STATUSES = ["Available", "Reserved", "Sold"];
const STATUS_LT = { Available: "Laisvas", Reserved: "Rezervuotas", Sold: "Parduotas" };
const ORIENTATIONS = ["north", "south", "east", "west", "northeast", "northwest", "southeast", "southwest"];
const ORIENT_LT = {
  north: "Šiaurė", south: "Pietūs", east: "Rytai", west: "Vakarai",
  northeast: "Šiaurės rytai", northwest: "Šiaurės vakarai", southeast: "Pietryčiai", southwest: "Pietvakariai",
};
const TERRACE_TYPES = { terrace: "Terasa", loggia: "Lodžija", balcony: "Balkonas", none: "Nėra" };
const FEATURES = {
  elevator: "Liftas", separateEntrance: "Atskiras įėjimas", highCeilings: "Aukštos lubos",
  newSewage: "Nauja kanalizacija", newWiring: "Nauja elektros instaliacija", enclosedYard: "Uždaras kiemas",
  internet: "Internetas", kitchenLiving: "Virtuvė sujungta su svetaine",
};
const EQUIPMENT = {
  airConditioning: "Oro kondicionierius", underfloorHeating: "Grindinis šildymas",
  plasticPipes: "Plastikiniai vamzdžiai", heatRecovery: "Rekuperacinė sistema",
};
const SECURITY = { armoredDoor: "Šarvuotos durys", alarm: "Signalizacija", codedLock: "Kodinė spyna", cctv: "Vaizdo stebėjimas" };
const ADDITIONAL = { terrace: "Terasa", loggia: "Lodžija", balcony: "Balkonas", none: "Nėra" };

const inputCls = "w-full bg-white border border-[#E4E4E7] rounded-lg px-3 py-2 text-sm text-[#1A1A1A] outline-none focus:border-[#D4AF37]";
const labelCls = "block text-[11px] uppercase tracking-wide text-[#52525B] mb-1";

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#52525B] border-[#E4E4E7] hover:bg-[#F5F3EE]"}`}>
      {children}
    </button>
  );
}

export default function AdminApartments() {
  const { buildings, refresh } = useBuildings();
  const [bKey, setBKey] = useState("Migdolas");
  const [code, setCode] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newUnit, setNewUnit] = useState("");
  const fileRef = useRef(null);

  const units = useMemo(() => (buildings[bKey]?.apartments || []), [buildings, bKey]);

  const blankForm = () => ({
    price: "", loggiaPrice: "", loggiaPricePerM2: "", showPricePerM2: false, rooms: "", floor: "", floors: 5, status: "Available",
    terraceType: "terrace", area: "", apartmentArea: "", terraceArea: "", loggiaArea: "",
    landShare: "", landShareNA: false, orientation: [],
    yearBuilt: 2026, finishing: "partial", buildingType: "block", heating: "centralElectric", energyClass: "A++",
    features: [...PROJECT_SPEC_DEFAULTS.features], additionalSpaces: ["terrace"],
    equipment: [...PROJECT_SPEC_DEFAULTS.equipment], security: [...PROJECT_SPEC_DEFAULTS.security],
    photos: [], floorPlanIndex: 0,
  });

  const loadUnit = async (c) => {
    setIsNew(false);
    setCode(c);
    if (!c) { setForm(null); return; }
    const apt = units.find((a) => a.code === c);
    if (!apt) return;
    let d = {};
    try { const res = await api.get(`/admin/apartments/${encodeURIComponent(c)}`); d = res.data || {}; } catch (e) {}
    const spec = getApartmentSpec(apt);
    const photos = apt._dbPhotos ? [...apt._dbPhotos] : [apt.sheet, ...(apt.photos || [])].filter(Boolean);
    setForm({
      price: apt.price ?? "",
      loggiaPrice: apt.loggiaPrice ?? "",
      loggiaPricePerM2: apt.loggiaPricePerM2 ?? "",
      showPricePerM2: apt.showPricePerM2 ?? false,
      rooms: apt.rooms ?? "",
      floor: apt.floor ?? "",
      floors: apt.floors ?? 5,
      status: d.status || apt.defaultStatus || "Available",
      terraceType: apt.terraceType || "terrace",
      area: apt.area ?? "",
      apartmentArea: apt.apartmentArea ?? "",
      terraceArea: apt.terraceArea ?? "",
      loggiaArea: apt.loggiaArea ?? "",
      landShare: apt.landShare ?? "",
      landShareNA: apt.landShareNA ?? false,
      orientation: (apt.orientationKeys && apt.orientationKeys.length ? apt.orientationKeys : spec.orientation) || [],
      yearBuilt: spec.yearBuilt || 2026,
      finishing: spec.finishing || "partial",
      buildingType: spec.buildingType || "block",
      heating: spec.heating || "centralElectric",
      energyClass: spec.energyClass || "A++",
      features: spec.features || [],
      additionalSpaces: spec.additionalSpaces || [],
      equipment: spec.equipment || [],
      security: spec.security || [],
      photos,
      floorPlanIndex: apt._dbPhotos ? (apt._floorPlanIndex ?? 0) : 0,
    });
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
  }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, photos: [...f.photos, ...urls] }));
      toast.success(`${urls.length} nuotrauka(-os) įkelta`);
    } catch (err) {
      toast.error("Nepavyko įkelti nuotraukos");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const movePhoto = (i, dir) => {
    setForm((f) => {
      const p = [...f.photos];
      const j = i + dir;
      if (j < 0 || j >= p.length) return f;
      [p[i], p[j]] = [p[j], p[i]];
      let fp = f.floorPlanIndex;
      if (fp === i) fp = j; else if (fp === j) fp = i;
      return { ...f, photos: p, floorPlanIndex: fp };
    });
  };
  const deletePhoto = (i) => {
    setForm((f) => {
      const p = f.photos.filter((_, idx) => idx !== i);
      let fp = f.floorPlanIndex;
      if (fp === i) fp = 0; else if (fp > i) fp -= 1;
      return { ...f, photos: p, floorPlanIndex: p.length ? fp : 0 };
    });
  };

  const save = async () => {
    const required = { area: form.area, rooms: form.rooms, floor: form.floor, status: form.status };
    for (const [k, v] of Object.entries(required)) {
      if (v === "" || v === null || v === undefined) { toast.error("Užpildykite privalomus laukus (Plotas, Kambariai, Aukštas, Statusas)"); return; }
    }
    if (!form.orientation.length) { toast.error("Pasirinkite bent vieną langų orientaciją"); return; }
    setSaving(true);
    try {
      const label = code.split(" ").pop();
      const num = parseInt(label.replace(/\D/g, ""), 10);
      const payload = {
        price: form.price === "" ? null : Number(form.price),
        loggiaPrice: form.loggiaPrice === "" ? null : Number(form.loggiaPrice),
        loggiaPricePerM2: form.loggiaPricePerM2 === "" ? null : Number(form.loggiaPricePerM2),
        showPricePerM2: form.showPricePerM2,
        ...(isNew ? { building: bKey, unit: label, custom: true, ...(num ? { number: num } : {}) } : {}),
        rooms: Number(form.rooms),
        floor: Number(form.floor),
        floors: Number(form.floors),
        status: form.status,
        terraceType: form.terraceType,
        area: Number(form.area),
        apartmentArea: form.apartmentArea === "" ? null : Number(form.apartmentArea),
        terraceArea: form.terraceArea === "" ? null : Number(form.terraceArea),
        loggiaArea: form.loggiaArea === "" ? null : Number(form.loggiaArea),
        landShare: form.landShare === "" ? null : Number(form.landShare),
        landShareNA: form.landShareNA,
        orientation: form.orientation,
        yearBuilt: Number(form.yearBuilt),
        finishing: form.finishing,
        buildingType: form.buildingType,
        heating: form.heating,
        energyClass: form.energyClass,
        features: form.features,
        additionalSpaces: form.additionalSpaces,
        equipment: form.equipment,
        security: form.security,
        photos: form.photos,
        floorPlanIndex: form.floorPlanIndex,
      };
      await api.put(`/admin/apartments/${encodeURIComponent(code)}`, payload);
      await refresh();
      setIsNew(false);
      toast.success(`${code} išsaugotas sėkmingai`);
    } catch (err) {
      toast.error("Nepavyko išsaugoti");
    } finally {
      setSaving(false);
    }
  };

  const createNew = () => {
    const label = newUnit.trim();
    if (!label) { toast.error("Įveskite buto numerį (pvz. B26)"); return; }
    const newCode = `${bKey} ${label}`;
    if (units.some((a) => a.code === newCode)) { toast.error("Toks butas jau egzistuoja"); return; }
    setCode(newCode);
    setForm(blankForm());
    setIsNew(true);
    setShowAdd(false);
    setNewUnit("");
    toast.success(`Naujas butas ${label} — užpildykite ir išsaugokite`);
  };

  const discardChanges = () => {
    if (isNew) { setForm(null); setCode(""); setIsNew(false); }
    else { loadUnit(code); }
    toast("Pakeitimai atšaukti");
  };

  const resetDefault = async () => {
    if (!window.confirm("Atstatyti šio buto duomenis į numatytuosius? Visi išsaugoti pakeitimai bus pašalinti.")) return;
    try {
      await api.delete(`/admin/apartments/${encodeURIComponent(code)}`);
      await refresh();
      toast.success("Atstatyta į numatytąjį. Pasirinkite butą iš naujo.");
      setForm(null); setCode(""); setIsNew(false);
    } catch (e) {
      toast.error("Nepavyko atstatyti");
    }
  };

  const isLoggia = form?.terraceType === "loggia";
  const spaceWordLt = form ? ({ loggia: "lodžija", terrace: "terasa", balcony: "balkonu" }[form.terraceType] || "erdvė") : "";

  return (
    <div data-testid="apartments-panel" className="space-y-6">
      <h2 className="font-serif text-2xl text-[#1A1A1A]">Butų valdymas</h2>

      {/* Selectors */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <select data-testid="apt-building-select" value={bKey} onChange={(e) => { setBKey(e.target.value); setCode(""); setForm(null); setIsNew(false); }} className={inputCls + " sm:max-w-[200px]"}>
          <option value="Migdolas">Migdolas</option>
          <option value="Vyšnia">Vyšnia</option>
        </select>
        <select data-testid="apt-unit-select" value={isNew ? "" : code} onChange={(e) => loadUnit(e.target.value)} className={inputCls + " sm:max-w-[240px]"}>
          <option value="">— Pasirinkite butą —</option>
          {units.map((a) => <option key={a.code} value={a.code}>{a.unit} · {a.rooms} kamb. · {a.area} m²</option>)}
        </select>
        <button type="button" data-testid="apt-add-btn" onClick={() => setShowAdd((v) => !v)} className="inline-flex items-center gap-2 bg-white border border-[#E4E4E7] text-[#1A1A1A] text-sm px-4 py-2 rounded-lg hover:bg-[#F5F3EE]">
          <Plus size={15} /> Pridėti butą
        </button>
      </div>

      {showAdd && (
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center bg-white border border-[#E4E4E7] rounded-lg p-4" data-testid="apt-add-row">
          <span className="text-sm text-[#52525B]">{bKey} ·</span>
          <input data-testid="apt-new-label" value={newUnit} onChange={(e) => setNewUnit(e.target.value)} placeholder={bKey === "Migdolas" ? "pvz. A21" : "pvz. B26"} className={inputCls + " sm:max-w-[160px]"} />
          <button type="button" data-testid="apt-create-btn" onClick={createNew} className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2B2B2B]">Sukurti</button>
        </div>
      )}

      {form && (
        <div className="bg-white border border-[#E4E4E7] rounded-lg p-6 space-y-8" data-testid="apt-form">
          {isNew && <p className="text-sm text-[#8a6d1a] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded px-3 py-2" data-testid="apt-new-banner">Naujas butas: <b>{code}</b> — užpildykite laukus ir išsaugokite.</p>}
          {/* Pricing */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#8a6d1a] mb-3">Kaina</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Kaina (€)</label><input data-testid="f-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} /></div>
              <label className="flex items-center gap-2 text-sm text-[#1A1A1A] mt-6">
                <input data-testid="f-showppm" type="checkbox" checked={form.showPricePerM2} onChange={(e) => set("showPricePerM2", e.target.checked)} /> Rodyti kainą už m²
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className={labelCls}>Buto su {spaceWordLt} kaina (€)</label>
                <input data-testid="f-loggiaprice" type="number" value={form.loggiaPrice} onChange={(e) => set("loggiaPrice", e.target.value)} placeholder="pvz. 127486" className={inputCls} />
                <p className="text-[11px] text-[#52525B] mt-1">Rodoma dideliu aukso šriftu: „{form.loggiaPrice ? Number(form.loggiaPrice).toLocaleString("lt-LT") : "127 486"} € buto su {spaceWordLt} kaina". Palikite tuščią — kaina nerodoma.</p>
              </div>
              <div>
                <label className={labelCls}>Kaina už m² su {spaceWordLt} (€/m²)</label>
                <input data-testid="f-loggiappm" type="number" value={form.loggiaPricePerM2} onChange={(e) => set("loggiaPricePerM2", e.target.value)} placeholder="pvz. 2500" className={inputCls} />
                <p className="text-[11px] text-[#52525B] mt-1">Antraeilė eilutė: „{form.loggiaPricePerM2 ? Number(form.loggiaPricePerM2).toLocaleString("lt-LT") : "2 500"} €/m² su {spaceWordLt} kaina".</p>
              </div>
            </div>
          </section>

          {/* Areas */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#8a6d1a] mb-3">Plotas</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className={labelCls}>{isLoggia ? "Bendras plotas su lodžija (m²) *" : "Bendras plotas (m²) *"}</label><input data-testid="f-area" type="number" value={form.area} onChange={(e) => set("area", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Buto plotas (m²)</label><input data-testid="f-aptarea" type="number" value={form.apartmentArea} onChange={(e) => set("apartmentArea", e.target.value)} className={inputCls} /></div>
              <div>
                <label className={labelCls}>Papildoma erdvė</label>
                <select data-testid="f-terracetype" value={form.terraceType} onChange={(e) => set("terraceType", e.target.value)} className={inputCls}>
                  {Object.entries(TERRACE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {!isLoggia && <div><label className={labelCls}>Terasos plotas (m²)</label><input data-testid="f-terracearea" type="number" value={form.terraceArea} onChange={(e) => set("terraceArea", e.target.value)} className={inputCls} /></div>}
              {isLoggia && <div><label className={labelCls}>Lodžijos plotas (m²)</label><input data-testid="f-loggiaarea" type="number" value={form.loggiaArea} onChange={(e) => set("loggiaArea", e.target.value)} className={inputCls} /></div>}
              <div>
                <label className={labelCls}>Nuosava žemės dalis (m²)</label>
                <input data-testid="f-landshare" type="number" value={form.landShare} disabled={form.landShareNA} onChange={(e) => set("landShare", e.target.value)} className={inputCls + (form.landShareNA ? " opacity-50" : "")} />
                <label className="flex items-center gap-2 text-xs text-[#52525B] mt-1">
                  <input data-testid="f-landna" type="checkbox" checked={form.landShareNA} onChange={(e) => set("landShareNA", e.target.checked)} /> Netaikoma
                </label>
              </div>
            </div>
          </section>

          {/* Basic */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#8a6d1a] mb-3">Pagrindinė informacija</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div><label className={labelCls}>Kambariai *</label><input data-testid="f-rooms" type="number" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Aukštas *</label><input data-testid="f-floor" type="number" value={form.floor} onChange={(e) => set("floor", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Aukštų pastate</label><input data-testid="f-floors" type="number" value={form.floors} onChange={(e) => set("floors", e.target.value)} className={inputCls} /></div>
              <div>
                <label className={labelCls}>Statusas *</label>
                <select data-testid="f-status" value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                  {INV_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LT[s]}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Langų orientacija *</label>
              <div className="flex flex-wrap gap-2">
                {ORIENTATIONS.map((o) => <Chip key={o} testid={`f-orient-${o}`} active={form.orientation.includes(o)} onClick={() => toggleArr("orientation", o)}>{ORIENT_LT[o]}</Chip>)}
              </div>
            </div>
          </section>

          {/* Extended specs */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#8a6d1a] mb-3">Išplėstinė specifikacija</h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div><label className={labelCls}>Statybos metai</label><input data-testid="f-year" type="number" value={form.yearBuilt} onChange={(e) => set("yearBuilt", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Įrengimas</label><input data-testid="f-finishing" value={form.finishing} onChange={(e) => set("finishing", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Pastato tipas</label><input data-testid="f-buildingtype" value={form.buildingType} onChange={(e) => set("buildingType", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Šildymas</label><input data-testid="f-heating" value={form.heating} onChange={(e) => set("heating", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Energinė klasė</label><input data-testid="f-energy" value={form.energyClass} onChange={(e) => set("energyClass", e.target.value)} className={inputCls} /></div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Ypatybės</label>
              <div className="flex flex-wrap gap-2">{Object.entries(FEATURES).map(([k, v]) => <Chip key={k} testid={`f-feat-${k}`} active={form.features.includes(k)} onClick={() => toggleArr("features", k)}>{v}</Chip>)}</div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Papildoma įranga</label>
              <div className="flex flex-wrap gap-2">{Object.entries(EQUIPMENT).map(([k, v]) => <Chip key={k} testid={`f-eq-${k}`} active={form.equipment.includes(k)} onClick={() => toggleArr("equipment", k)}>{v}</Chip>)}</div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Sauga</label>
              <div className="flex flex-wrap gap-2">{Object.entries(SECURITY).map(([k, v]) => <Chip key={k} testid={`f-sec-${k}`} active={form.security.includes(k)} onClick={() => toggleArr("security", k)}>{v}</Chip>)}</div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Papildomos erdvės</label>
              <div className="flex flex-wrap gap-2">{Object.entries(ADDITIONAL).map(([k, v]) => <Chip key={k} testid={`f-add-${k}`} active={form.additionalSpaces.includes(k)} onClick={() => toggleArr("additionalSpaces", k)}>{v}</Chip>)}</div>
            </div>
          </section>

          {/* Photos */}
          <section>
            <h3 className="text-sm uppercase tracking-widest text-[#8a6d1a] mb-3">Nuotraukos</h3>
            <p className="text-xs text-[#52525B] mb-3">Pažymėkite vieną nuotrauką kaip „Buto planą" (rodoma viršuje). Tvarka nustato galerijos eiliškumą.</p>
            <input ref={fileRef} data-testid="f-upload" type="file" accept="image/*" multiple onChange={onUpload} className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="f-upload-btn" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-wide px-4 py-2 rounded hover:bg-[#2B2B2B] disabled:opacity-50 mb-4">
              <Upload size={14} /> {uploading ? "Keliama..." : "Įkelti nuotraukas"}
            </button>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {form.photos.map((p, i) => (
                <div key={i} className={`border rounded-lg overflow-hidden ${form.floorPlanIndex === i ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : "border-[#E4E4E7]"}`} data-testid={`f-photo-${i}`}>
                  <div className="aspect-[4/3] bg-[#F5F3EE] flex items-center justify-center">
                    <img src={toAbsUrl(p)} alt="" className="w-full h-full object-contain" />
                  </div>
                  {form.floorPlanIndex === i && <p className="text-[10px] text-center text-[#8a6d1a] py-1 uppercase tracking-wide flex items-center justify-center gap-1"><ImageIcon size={11} /> Buto planas</p>}
                  <div className="flex items-center justify-between px-2 py-1.5 gap-1 bg-white">
                    <button type="button" onClick={() => movePhoto(i, -1)} data-testid={`f-up-${i}`} className="text-[#52525B] hover:text-[#1A1A1A]" title="Aukštyn"><ArrowUp size={15} /></button>
                    <button type="button" onClick={() => movePhoto(i, 1)} data-testid={`f-down-${i}`} className="text-[#52525B] hover:text-[#1A1A1A]" title="Žemyn"><ArrowDown size={15} /></button>
                    <button type="button" onClick={() => set("floorPlanIndex", i)} data-testid={`f-setfp-${i}`} className={form.floorPlanIndex === i ? "text-[#D4AF37]" : "text-[#52525B] hover:text-[#D4AF37]"} title="Nustatyti kaip buto planą"><Star size={15} fill={form.floorPlanIndex === i ? GOLD : "none"} /></button>
                    <button type="button" onClick={() => deletePhoto(i)} data-testid={`f-del-${i}`} className="text-red-500 hover:text-red-700" title="Ištrinti"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
              {form.photos.length === 0 && <p className="text-sm text-[#52525B] col-span-full py-6 text-center">Nuotraukų nėra</p>}
            </div>
          </section>

          {/* Save */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 border-t border-[#E4E4E7] pt-5">
            <div className="flex gap-2">
              <button type="button" onClick={discardChanges} data-testid="f-discard" className="inline-flex items-center gap-2 bg-white border border-[#E4E4E7] text-[#52525B] text-sm px-4 py-3 rounded hover:bg-[#F5F3EE]">
                <Undo2 size={15} /> Atšaukti pakeitimus
              </button>
              {!isNew && (
                <button type="button" onClick={resetDefault} data-testid="f-reset" className="inline-flex items-center gap-2 bg-white border border-red-200 text-red-600 text-sm px-4 py-3 rounded hover:bg-red-50">
                  <RotateCcw size={15} /> Atstatyti numatytąjį
                </button>
              )}
            </div>
            <button type="button" onClick={save} disabled={saving} data-testid="f-save" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#1A1A1A] text-sm font-medium uppercase tracking-wide px-6 py-3 rounded hover:bg-[#c39f2f] disabled:opacity-50">
              <Save size={16} /> {saving ? "Saugoma..." : "Išsaugoti"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
